import { useContext } from 'react'

import { cn } from '@/lib/utils'

import { StackedBarChartContext } from './stacked-bar-chart-context'

const STACKED_BAR_HALO_SIZE = 4

interface Props {
  data: { value: number }[]
}

export const StackedBarChartBar: React.FC<Props> = ({ data }) => {
  const ctx = useContext(StackedBarChartContext)

  if (!ctx) {
    throw new Error('<StackedBarChartBar> must be inside a <StackedBarChart>')
  }

  const total = data.reduce((acc, d) => acc + d.value, 0)

  return (
    <div
      className={cn('relative', 'flex', 'rounded-md', 'bg-[#f1f3f5]')}
      style={{
        width: ctx.width,
        height: ctx.height,
      }}
    >
      {data.map((d, i) => {
        const hovered = ctx.hovered ?? ctx.tooltipHovered ?? null
        const isHovered = i === hovered?.index
        const isAnyHovered = hovered != null
        const colors = ctx.colors
        const xBegin = (data.slice(0, i).reduce((sum, d) => sum + d.value, 0) / total) * ctx.width
        const xEnd = (data.slice(0, i + 1).reduce((sum, d) => sum + d.value, 0) / total) * ctx.width
        const color = colors[i % colors.length]

        return (
          <div
            key={i}
            className={cn('relative', {
              'cursor-pointer': !hovered?.locked,
              'rounded-l-md': i === 0,
              'rounded-r-md': i === data.length - 1,
            })}
            style={{
              width: `${(d.value / total) * 100}%`,
              backgroundColor: color,
              transition: 'width 160ms ease-out',
            }}
            onMouseEnter={() => {
              ctx.setHovered((prev) => {
                if (!prev) {
                  return { index: i, xBegin, xEnd, locked: false }
                }
                if (prev.locked) {
                  return prev
                }
                return { index: i, xBegin, xEnd, locked: false }
              })
            }}
            onClick={() => {
              ctx.setHovered((prev) => {
                if (!prev) {
                  return { index: i, xBegin, xEnd, locked: true }
                }
                if (prev.locked) {
                  return { index: i, xBegin, xEnd, locked: !prev.locked }
                }
                return { index: i, xBegin, xEnd, locked: true }
              })
            }}
          >
            {/* Backdrop for hover effect */}
            <div
              className={cn('absolute', 'pointer-events-none', {
                'rounded-l-lg': i === 0,
                'rounded-r-lg': i === data.length - 1,
              })}
              style={{
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                backgroundColor: 'black',
                opacity: isAnyHovered && !isHovered ? 0.5 : 0,
                transitionTimingFunction: 'cubic-bezier(0,0,0.35,1)',
                transitionProperty: 'opacity',
                transitionDuration: '80ms',
              }}
            />

            {/* Halo for hover effect */}
            <div
              className={cn('absolute', 'pointer-events-none', {
                'rounded-l-lg': i === 0,
                'rounded-r-lg': i === data.length - 1,
              })}
              style={{
                top: -STACKED_BAR_HALO_SIZE,
                right: i === data.length - 1 ? -STACKED_BAR_HALO_SIZE : 0,
                bottom: -STACKED_BAR_HALO_SIZE,
                left: i === 0 ? -STACKED_BAR_HALO_SIZE : 0,
                backgroundColor: color,
                opacity: isHovered ? 0.3 : 0,
                transitionTimingFunction: 'cubic-bezier(0,0,0.35,1)',
                transitionProperty: 'opacity',
                transitionDuration: '80ms',
              }}
            />
          </div>
        )
      })}
    </div>
  )
}
StackedBarChartBar.displayName = 'StackedBarChartBar'
